import { GuideContext, GuideStep, GuideTour } from './guide.types';

/**
 * Guides for people who have not finished them yet.
 * Add a tour here, then mark each target with `appGuideAnchor="the-anchor-id"`.
 * The tour id is stored on the user when they finish or skip it.
 */
export const GUIDE_TOURS: GuideTour[] = [
  {
    id: 'welcome',
    steps: [
      {
        id: 'menu',
        anchor: 'toolbar-menu',
        title: 'Menú',
        body: 'Desde aquí abres las secciones de este consultorio.',
      },
      {
        id: 'patients',
        anchor: 'nav-patients',
        openMenu: true,
        when: (ctx) => ctx.clinical,
        title: 'Pacientes',
        body: 'Aquí está el expediente de cada paciente: datos, consultas y documentos.',
      },
      {
        id: 'agenda',
        anchor: 'nav-agenda',
        openMenu: true,
        when: (ctx) => ctx.clinical,
        title: 'Agenda',
        body: 'Las citas del consultorio se programan desde la agenda.',
      },
      {
        id: 'consultas',
        anchor: 'nav-consultas',
        openMenu: true,
        when: (ctx) => ctx.doctor,
        title: 'Consultas',
        body: 'Las notas de consulta de este consultorio viven aquí.',
      },
      {
        id: 'clinic',
        anchor: 'toolbar-clinic',
        title: 'Consultorio',
        body: 'Este es el consultorio activo. Si tienes más de uno, desde aquí cambias.',
      },
    ],
  },
  {
    id: 'informacion',
    after: ['welcome'],
    audience: (ctx) => ctx.owner,
    steps: [
      {
        id: 'patients',
        anchor: 'patient-fields',
        route: '/consultorio',
        consultorioTab: 'patients',
        title: 'Información de pacientes',
        body: 'Aquí eliges qué datos del paciente se guardan. Puedes ordenar los campos, cambiar su nombre, ocultarlos, eliminarlos o agregar otros. Los cambios se guardan solos.',
      },
      {
        id: 'appointments',
        anchor: 'appointment-fields',
        route: '/consultorio',
        consultorioTab: 'appointments',
        title: 'Información de consultas',
        body: 'En cada consulta defines qué información se captura y se guarda. Puedes ocultar o eliminar campos. La fecha, las recetas y los archivos siempre se incluyen.',
      },
    ],
  },
  {
    id: 'plantillas',
    after: ['welcome'],
    audience: (ctx) => ctx.clinical,
    when: (ctx) => ctx.patients >= 1,
    steps: [
      {
        id: 'intro',
        anchor: 'plantillas-intro',
        route: '/plantillas',
        title: 'Plantillas',
        body: 'Sirven para generar los documentos que necesitas seguido para un paciente. Al usarlas, se llenan con sus datos.',
      },
      {
        id: 'patient-file',
        anchor: 'patient-generate-document',
        action: 'open-patient-file',
        title: 'Desde el expediente',
        body: 'Abre el archivo del paciente y genera el documento desde este botón.',
      },
      {
        id: 'appointment',
        anchor: 'appointment-generate-document',
        action: 'open-patient-appointment',
        route: '/appointments',
        when: (ctx) => ctx.doctor,
        title: 'Desde la consulta',
        body: 'En la consulta de ese paciente también puedes generarlo, con la nota de esta visita.',
      },
    ],
  },
  {
    id: 'cobros',
    after: ['welcome', 'plantillas'],
    audience: (ctx) => ctx.clinical,
    when: (ctx) => ctx.invoicing,
    steps: [
      {
        id: 'patient-bill',
        anchor: 'patient-new-bill',
        action: 'open-patient-file',
        patientTab: 'cuenta',
        title: 'Cobros',
        body: 'Un cobro anota lo que el paciente debe. El saldo de la cuenta es la diferencia entre cobros y pagos.',
      },
      {
        id: 'patient-payment',
        anchor: 'patient-register-payment',
        action: 'open-patient-file',
        patientTab: 'cuenta',
        title: 'Pagos',
        body: 'Un pago anota lo que el paciente ya te pagó. Juntos, cobros y pagos llevan la cuenta del expediente.',
      },
      {
        id: 'appointment',
        anchor: 'appointment-new-bill',
        action: 'open-patient-appointment',
        route: '/appointments',
        when: (ctx) => ctx.doctor,
        title: 'Cobro en la consulta',
        body: 'En la consulta también registras el cobro de esta visita. El pago se anota en la cuenta del paciente.',
      },
    ],
  },
  {
    id: 'facturacion',
    after: ['welcome', 'plantillas', 'cobros'],
    audience: (ctx) => ctx.owner,
    when: (ctx) => ctx.invoicing,
    steps: [
      {
        id: 'profile',
        anchor: 'facturacion-profile',
        route: '/facturacion',
        facturacionTab: 'config',
        title: 'Datos de facturación',
        body: 'Sube la constancia de situación fiscal, o captura el RFC, la razón social, el régimen y el código postal, junto con la e.firma. La revisión puede tardar hasta 2 días hábiles.',
      },
      {
        id: 'products',
        anchor: 'facturacion-products',
        route: '/facturacion',
        facturacionTab: 'catalog',
        title: 'Productos para facturar',
        body: 'En el catálogo SAT cargas los productos y servicios de la factura: clave, descripción y precio.',
      },
      {
        id: 'recipients',
        anchor: 'facturacion-recipients',
        route: '/facturacion',
        facturacionTab: 'recipients',
        title: 'Receptores',
        body: 'Registra a quién le facturas. Puedes subir su constancia para llenar el RFC, el régimen y el código postal.',
      },
    ],
  },
  {
    id: 'factura',
    after: ['welcome', 'plantillas', 'cobros', 'facturacion'],
    audience: (ctx) => ctx.owner || ctx.doctor || ctx.secretary || ctx.contador,
    when: (ctx) => ctx.invoicing && ctx.invoicingReady,
    steps: [
      {
        id: 'create',
        anchor: 'facturacion-create',
        route: '/facturacion',
        title: 'Crear factura',
        body: 'Tu perfil de facturación ya está autorizado. La factura se genera con este botón.',
      },
    ],
  },
];

export function guideTour(id: string): GuideTour | undefined {
  return GUIDE_TOURS.find((tour) => tour.id === id);
}

/** Steps this person is allowed to see. */
export function visibleSteps(tour: GuideTour, ctx: GuideContext): GuideStep[] {
  return tour.steps.filter((step) => !step.when || step.when(ctx));
}

/** False when this person can never take the tour, so later tours do not wait on it. */
export function tourApplies(tour: GuideTour, ctx: GuideContext): boolean {
  if (tour.audience && !tour.audience(ctx)) {
    return false;
  }
  return visibleSteps(tour, ctx).length > 0;
}

/** True when every applicable earlier tour is done and any extra condition passes. */
export function isTourReady(tour: GuideTour, ctx: GuideContext): boolean {
  if (!tourApplies(tour, ctx)) {
    return false;
  }
  const waitingOn = (tour.after ?? []).some((id) => {
    const earlier = guideTour(id);
    return !!earlier && tourApplies(earlier, ctx) && !ctx.completed.has(id);
  });
  if (waitingOn) {
    return false;
  }
  return tour.when ? tour.when(ctx) : true;
}
